import React from 'react';
import PropTypes from 'prop-types';
import { Info } from 'common/images/FaIcons';
import { useTranslation } from 'react-i18next';
import {
  ThresholdsDisplayContainer,
  ThresholdsHeader,
  ThresholdsGroup,
  ThresholdGroupItem,
  ThresholdLabel,
  ThresholdValue,
  ThresholdBadge,
  ThresholdDirections,
  DirectionItem,
  ThresholdValueContainer
} from './styled';

const ThresholdsDisplay = ({ alarmDisplays }) => {
  const { t } = useTranslation();
  if (!alarmDisplays || alarmDisplays.length === 0) {
    return null;
  }

  // Group thresholds by sensor type and feature
  const temperatureThresholds = alarmDisplays.filter(item => item.type === 'Temperature');
  const vibrationThresholds = alarmDisplays.filter(item => item.type.startsWith('Vibration'));

  // Group vibration thresholds by feature
  const vibrationByFeature = {};
  vibrationThresholds.forEach((item) => {
    const match = item.type.match(/Vibration ([\w\s]+) \(/);
    const feature = match ? match[1] : 'Vibration';
    if (!vibrationByFeature[feature]) {
      vibrationByFeature[feature] = [];
    }
    vibrationByFeature[feature].push(item);
  });

  return (
    <ThresholdsDisplayContainer>
      <ThresholdsHeader>
        <Info />
        <span>{t('sensors.eventDetection.coupling.currentAlarmThresholds')}</span>
      </ThresholdsHeader>
      <ThresholdsGroup>
        {/* Temperature */}
        {temperatureThresholds.map((item, idx) => (
          <ThresholdGroupItem key={`temp-${idx}`}>
            <ThresholdLabel>{t('sensors.eventDetection.temperature')}</ThresholdLabel>
            <ThresholdValue>{item.value}</ThresholdValue>
            <ThresholdBadge level={item.level}>{item.level}</ThresholdBadge>
          </ThresholdGroupItem>
        ))}

        {/* Vibration by feature */}
        {Object.entries(vibrationByFeature).map(([feature, items]) => {
          // Extract direction labels and values
          const directionItems = items.map((item) => {
            const match = item.type.match(/\(([^)]+)\)/);
            return {
              direction: match ? match[1] : '',
              value: item.value,
              level: item.level
            };
          });

          // Check if all items have the same value (common case)
          const allSameValue = directionItems.length > 0 &&
            directionItems.every(item => item.value === directionItems[0].value);

          return (
            <ThresholdGroupItem key={feature}>
              <ThresholdLabel>{feature}</ThresholdLabel>
              {allSameValue ? (
                <>
                  <ThresholdValueContainer>
                    <ThresholdValue>{directionItems[0].value}</ThresholdValue>
                    <ThresholdDirections>
                      {directionItems.map((item, dirIdx) => (
                        <DirectionItem key={dirIdx}>{item.direction}</DirectionItem>
                      ))}
                    </ThresholdDirections>
                  </ThresholdValueContainer>
                  <ThresholdBadge level={directionItems[0].level}>
                    {directionItems[0].level}
                  </ThresholdBadge>
                </>
              ) : (
                <>
                  <ThresholdDirections>
                    {directionItems.map((item, dirIdx) => (
                      <DirectionItem key={dirIdx}>
                        {item.direction}: <ThresholdValue>{item.value}</ThresholdValue>
                      </DirectionItem>
                    ))}
                  </ThresholdDirections>
                  <ThresholdBadge level={directionItems[0].level}>
                    {directionItems[0].level}
                  </ThresholdBadge>
                </>
              )}
            </ThresholdGroupItem>
          );
        })}
      </ThresholdsGroup>
    </ThresholdsDisplayContainer>
  );
};

ThresholdsDisplay.propTypes = {
  alarmDisplays: PropTypes.array
};

ThresholdsDisplay.defaultProps = {
  alarmDisplays: []
};

// Memoized because it only depends on alarmDisplays array which is computed via useMemo
export default React.memo(ThresholdsDisplay);
